import React, { ComponentProps, PropsWithChildren, useMemo } from "react";
import { Image } from "react-bootstrap";
import { Link } from "react-router-dom";

import "./GameTile.scss";
import { formatPlayCount } from "../../../utils/helpers";
import GhostButton from "../GhostButton/GhostButton";
import { ROUTES } from "../../../utils/constants";
import PlayIcon from "../../icons/PlayIcon";
import ImageFallbackThumbnail from "../../../images/fallback-thumbnail.png";

export enum GameTileVariant {
  LINK = "link",
  SELECT = "select",
}

interface GameTileProps extends ComponentProps<"div"> {
  name: string;
  slug?: string;
  thumbnail?: string;
  playCount?: number;
  playIconColor?: string;
  isSelected?: boolean;
  variant?: GameTileVariant;
  linkTo?: string;
  tileLabel?: string;
  onClick?: () => void;
}

function GameTileThumbnail({ src, label, name }: { src?: string; label?: string; name: string }) {
  return (
    <div className="thumbnail-container">
      <Image
        src={src || ImageFallbackThumbnail}
        className="d-block thumbnail"
        alt={name}
      />
      {label && <span className="tile-label">{label}</span>}
    </div>
  );
}

function GameTilePlayCount({
  playCount,
  iconColor,
}: {
  playCount?: number;
  iconColor: string;
}) {
  const hasCount = playCount != null && playCount > 0;

  return (
    <p className="d-flex align-items-center gap-1 count">
      {hasCount && (
        <>
          <PlayIcon
            width={14}
            height={14}
            fill={iconColor}
            className="play-icon"
          />
          {formatPlayCount(playCount!)}
        </>
      )}
    </p>
  );
}

function GameTileWrapper({
  children,
  variant,
  slug,
  linkTo,
  onClick,
}: PropsWithChildren<{
  variant: GameTileVariant;
  slug?: string;
  linkTo?: string;
  onClick?: () => void;
}>) {
  const link = useMemo(
    () => linkTo ?? `${ROUTES.GAMES.ROOT}/${slug}/play`,
    [slug, linkTo]
  );

  if (variant === GameTileVariant.SELECT || onClick) {
    return (
      <GhostButton
        onClick={onClick}
        className={
          onClick &&
          "text-start p-0 border-0 m-0 bg-transparent text-decoration-none flex-shrink-0"
        }
      >
        {children}
      </GhostButton>
    );
  }

  return (
    <Link to={link} className="text-decoration-none flex-shrink-0">
      {children}
    </Link>
  );
}

function GameTile({
  name,
  slug,
  thumbnail,
  playCount,
  playIconColor = "#6D6876",
  isSelected,
  variant = GameTileVariant.LINK,
  linkTo,
  tileLabel,
  className = "",
  onClick,
  ...restProps
}: GameTileProps) {
  return (
    <GameTileWrapper
      variant={variant}
      slug={slug}
      linkTo={linkTo}
      onClick={onClick}
    >
      <div
        className={`game-tile ${isSelected ? "selected" : ""} ${className}`}
        {...restProps}
      >
        <GameTileThumbnail src={thumbnail} label={tileLabel} name={name} />
        <p className="text-start text-capitalize title">{name}</p>
        <GameTilePlayCount playCount={playCount} iconColor={playIconColor} />
        {isSelected && (
          <GhostButton className="remove-btn">
            <span />
          </GhostButton>
        )}
      </div>
    </GameTileWrapper>
  );
}

export default GameTile;
